/**
 * ページ読み込み完了を通知する composable。
 *
 * 特徴：
 *  - 最小表示時間（MIN_DURATION）を保証
 *  - フェード演出（FADE_DURATION）付き
 *  - `usePageReady(async () => {...})` と `finishLoading()` の両対応
 *
 * 使い方：
 * ① ページ全体でタスクを待機したい場合
 *    await usePageReady(async () => {
 *      await longTask()
 *    })
 *
 * ② Vue 標準スタイルで書きたい場合
 *    const { finishLoading } = usePageReady()
 *    onMounted(async () => {
 *      await longTask()
 *      await finishLoading()
 *    })
 */
export function usePageReady(task?: () => Promise<void> | void) {
  const { stopLoading } = useGlobalLoading()
  const startTime = performance.now()

  const MIN_DURATION = 350 // ローディング最低表示時間(ms)
  const FADE_DURATION = 250 // フェード時間(ms)

  /**
   * ローディング終了処理。
   * 最小表示時間＋フェード演出を自動適用。
   */
  async function finishLoading() {
    const elapsed = performance.now() - startTime
    const remain = Math.max(MIN_DURATION - elapsed, 0)

    if (remain > 0) await delay(remain)

    await fadeOutOverlay(FADE_DURATION)
    stopLoading()
    await delay(FADE_DURATION)
  }

  // 旧来の「即タスク実行」形式にも対応
  if (task) {
    ;(async () => {
      try {
        await task()
      }
      finally {
        await finishLoading()
      }
    })()
  }

  return { finishLoading }
}

/**
 * 一定時間待機するユーティリティ
 */
function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * フェードアウトアニメーションを制御
 * （オーバーレイのopacityをCSS変化で滑らかに）
 */
async function fadeOutOverlay(duration: number) {
  const overlay = document.querySelector('.loading-overlay') as HTMLElement | null
  if (!overlay) return

  overlay.style.transition = `opacity ${duration}ms ease`
  overlay.style.opacity = '0'

  await delay(duration)

  overlay.style.display = 'none'
}
